type AeroCandidatePromotionEligibilityInput = {
    aeroExitCandidateId?: number | null;
    drawdown?: number | null;
    evaluationFailureCount?: number | null;
    promotedExitCandidateId: number | null;
    promotedExitCandidateStatus?: 'active' | 'retired' | 'invalid' | null;
    recoverableDataGapEvaluationCount?: number | null;
    selectionBlockingEvaluationCount?: number | null;
    selectionEligible?: boolean;
    totalTrades?: number | null;
};

export type AeroCandidateEligibilityBadgeVariant = 'success' | 'warning' | 'danger';

export interface AeroCandidateEligibilityPresentation {
    badgeVariant: AeroCandidateEligibilityBadgeVariant;
    detail: string;
    label: string;
}

/**
 * Mirrors the backend promotion gate without trying to recalculate its data-coverage policy.
 * `selectionEligible` remains the authority for evaluation failures and the recoverable-gap limit.
 */
export const isAeroCandidateSelectionSafe = (candidate: AeroCandidatePromotionEligibilityInput): boolean =>
    candidate.selectionEligible === true &&
    candidate.drawdown !== null &&
    candidate.drawdown !== undefined &&
    Number.isFinite(candidate.drawdown) &&
    candidate.drawdown < 100;

export const isAeroCandidatePromotable = (candidate: AeroCandidatePromotionEligibilityInput): boolean =>
    candidate.aeroExitCandidateId != null && isAeroCandidateSelectionSafe(candidate);

/** Entity drill-down needs complete evaluations, not portfolio-only publication metrics. */
export const isAeroCandidateVisibleByDefault = (
    candidate: AeroCandidatePromotionEligibilityInput,
    scope: 'portfolio' | 'entity'
): boolean => (scope === 'entity' ? candidate.selectionEligible === true : isAeroCandidateSelectionSafe(candidate));

export const getAeroRecoverableDataGapRatePercent = (candidate: AeroCandidatePromotionEligibilityInput): number | null => {
    const totalTrades = candidate.totalTrades;
    const blockingEvaluationCount = candidate.selectionBlockingEvaluationCount;
    const recoverableGapCount = candidate.recoverableDataGapEvaluationCount;

    if (
        totalTrades == null ||
        blockingEvaluationCount == null ||
        recoverableGapCount == null ||
        !Number.isFinite(totalTrades) ||
        !Number.isFinite(blockingEvaluationCount) ||
        !Number.isFinite(recoverableGapCount) ||
        totalTrades < 0 ||
        blockingEvaluationCount < 0 ||
        recoverableGapCount < 0
    ) {
        return null;
    }

    const evaluatedEntryCount = totalTrades + blockingEvaluationCount;
    return evaluatedEntryCount > 0 ? (recoverableGapCount / evaluatedEntryCount) * 100 : null;
};

export const getAeroCandidateEligibilityPresentation = (
    candidate: AeroCandidatePromotionEligibilityInput,
    scope: 'portfolio' | 'entity' = 'portfolio'
): AeroCandidateEligibilityPresentation => {
    if (candidate.evaluationFailureCount != null && candidate.evaluationFailureCount > 0) {
        return {
            badgeVariant: 'danger',
            detail: `${candidate.evaluationFailureCount} evaluation failure${candidate.evaluationFailureCount === 1 ? '' : 's'} blocked selection.`,
            label: 'Ineligible — evaluation failures'
        };
    }

    if (candidate.selectionEligible !== true) {
        if ((candidate.recoverableDataGapEvaluationCount ?? 0) > 0) {
            return {
                badgeVariant: 'danger',
                detail: 'The recorded backend policy rejected this coverage or another selection blocker. Older runs retain their recorded eligibility.',
                label: 'Ineligible — data coverage'
            };
        }

        if ((candidate.totalTrades ?? 0) === 0) {
            return {
                badgeVariant: 'danger',
                detail: 'No completed trades were available for selection.',
                label: 'Ineligible — no trades'
            };
        }

        return {
            badgeVariant: 'danger',
            detail: 'The backend marked this result ineligible for selection.',
            label: 'Ineligible'
        };
    }

    if (scope === 'entity') {
        return {
            badgeVariant: 'success',
            detail: 'Passed the recorded entity evaluation policy. Publication is assessed in the portfolio view.',
            label: 'Complete'
        };
    }

    if (candidate.drawdown == null || !Number.isFinite(candidate.drawdown)) {
        return {
            badgeVariant: 'danger',
            detail: 'A valid portfolio drawdown is required for selection.',
            label: 'Ineligible — missing drawdown'
        };
    }

    if (candidate.drawdown >= 100) {
        return {
            badgeVariant: 'danger',
            detail: 'Portfolio drawdown reached or exceeded 100%.',
            label: 'Ineligible — drawdown'
        };
    }

    const gapRate = getAeroRecoverableDataGapRatePercent(candidate);
    if (gapRate !== null && gapRate > 0.5) {
        return {
            badgeVariant: 'warning',
            detail: 'Recognized data gaps exceed 0.5% of opened/evaluable attempts. Check entity/time concentration and shortlist sensitivity before advancing; missing outcomes are not measured returns.',
            label: 'Eligible — coverage warning'
        };
    }

    return {
        badgeVariant: 'success',
        detail: 'Passed the backend data-coverage and drawdown selection gates.',
        label: 'Eligible'
    };
};

/** Only globally published candidates can be referenced by realized experiments. */
export const isAeroCandidateGloballyAvailable = (candidate: AeroCandidatePromotionEligibilityInput): boolean =>
    candidate.promotedExitCandidateId !== null && candidate.promotedExitCandidateStatus === 'active';

export const formatAeroCandidatePersistenceLabel = (candidate: AeroCandidatePromotionEligibilityInput): string => {
    if (candidate.promotedExitCandidateId === null) {
        return candidate.aeroExitCandidateId == null
            ? 'Not materialized'
            : `AERO candidate ${candidate.aeroExitCandidateId} · not published`;
    }
    if (candidate.promotedExitCandidateStatus === 'active') return `Global candidate ${candidate.promotedExitCandidateId}`;
    if (candidate.promotedExitCandidateStatus === 'retired') return `Retired global candidate ${candidate.promotedExitCandidateId}`;
    if (candidate.promotedExitCandidateStatus === 'invalid') return `Invalid global candidate ${candidate.promotedExitCandidateId}`;

    return `Global candidate ${candidate.promotedExitCandidateId} status unknown`;
};

export const getAeroCandidatePersistenceBadgeVariant = (
    candidate: AeroCandidatePromotionEligibilityInput
): 'success' | 'warning' | 'danger' => {
    if (candidate.promotedExitCandidateStatus === 'active') return 'success';
    if (candidate.promotedExitCandidateStatus === 'invalid') return 'danger';

    return 'warning';
};
